import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import { faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { thinScrollbarStyles } from 'shared/design-system/settings/variables/Scrollbar';
import {
  ProgramQuestionWithAnswer,
  useProgram,
} from 'shared/providers/ProgramProvider';
import { getAnswerText } from 'npo/pages/BetaFeedback/Feedback/FeedbackAnswer';
import { feedbackSectionIdToString } from '../../NPOFeedback/utils';
import { getSectionIcon } from 'shared/components/WelcomeBack/Sections';
import StatusIcon from './StatusIcon';

const Wrapper = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${SPACERS.large};
  gap: ${SPACERS.medium};
  overflow: auto;
  height: 100%;
  background-color: ${COLORS.grayLight};
  ${thinScrollbarStyles}

  .review-header {
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.tiny};
  }

  .section-card {
    background-color: ${COLORS.whiteBase};
    border: 1px solid ${COLORS.grayLight};
    border-radius: 4px;
    padding: ${SPACERS.medium};
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.small};
  }

  .section-title-row {
    display: flex;
    align-items: center;
    gap: ${SPACERS.tiny};
  }

  .question-row {
    display: grid;
    grid-template-columns: 22px 1fr auto;
    align-items: start;
    gap: ${SPACERS.small};
    padding: ${SPACERS.tiny} ${SPACERS.small};
    margin: 0 -${SPACERS.small};
    border-top: 1px solid ${COLORS.grayLightest};
    border-radius: 4px;
    transition: background-color 0.3s ease-in-out;
    &:hover {
      background-color: ${COLORS.grayLight};
    }
    &:first-of-type {
      border-top: none;
    }
  }

  .q-body {
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.extraTiny};
  }

  .q-label {
    color: ${COLORS.greyDark};
  }

  .q-answer.empty {
    color: ${COLORS.greyDark};
    font-style: italic;
  }

  .beneficiary-card {
    margin-top: ${SPACERS.tiny};
    margin-bottom: ${SPACERS.tiny};
    .card-details-heading {
      font-size: 14px;
      margin-bottom: ${SPACERS.tiny};
    }
    .card-row {
      .card-row-text {
        font-size: 12px;
        color: ${COLORS.greyDark};
      }
    }
  }
  .impact-summary-array-render {
    font-size: 13px;
    font-weight: bold;
  }
  .budget-file-render,
  .budget-summary-render {
    font-size: 13px;
  }
`;

/**
 * Resolve the plain-text value that `getAnswerText` should render for a
 * program question. Enum selections are mapped to their display label
 * via `enumResponses`. Returns an empty string when unanswered.
 */
const resolveAnswerText = (q: ProgramQuestionWithAnswer): string => {
  const a = q.answer;
  if (!a) return '';
  if (a.text) return a.text;
  if (a.answerId != null) {
    const match = q.enumResponses.find(o => o.dbId === a.answerId);
    return match?.response ?? a.questionText ?? '';
  }
  return '';
};

interface ReviewScreenProps {
  onEditQuestion: (questionId: number) => void;
}

const ReviewScreen: React.FC<ReviewScreenProps> = ({ onEditQuestion }) => {
  const {
    questions,
    isAllComplete,
    sectionStatus,
    questionStatus,
  } = useProgram();

  if (questions.type !== 'REQUEST_SUCCESS') {
    return null;
  }

  const { sections } = questions.data;

  return (
    <Wrapper>
      <div className="review-header">
        <TrueImpactText variant="heading-subheading">
          Review & Submit
        </TrueImpactText>
        <TrueImpactText variant="body-small" color="greyDark">
          {isAllComplete
            ? 'All questions answered. Submit when ready.'
            : 'Some questions are missing. You can still submit, but flagged rows will remain unanswered.'}
        </TrueImpactText>
      </div>

      {sections.map(section => {
        const label = section.section
          ? feedbackSectionIdToString(section.section)
          : 'Uncategorized';
        return (
          <div className="section-card" key={label}>
            <div className="section-title-row">
              <StatusIcon status={sectionStatus(section)} />
              {section.section && (
                <FontAwesomeIcon
                  icon={getSectionIcon(section.section)}
                  // color={COLORS.ctaPrimary}
                />
              )}
              <TrueImpactText variant="body-bold">{label}</TrueImpactText>
              <TrueImpactText variant="body-small" color="greyDark">
                {section.answered}/{section.total}
              </TrueImpactText>
            </div>
            {section.questions.map(q => {
              const answerText = resolveAnswerText(q);
              const rendered = answerText
                ? getAnswerText(q.questionId, { text: answerText })
                : undefined;
              return (
                <div className="question-row" key={q.questionId}>
                  <StatusIcon status={questionStatus(q)} size={16} />
                  <div className="q-body">
                    <TrueImpactText variant="body-small" className="q-label">
                      {q.textNode ||
                        q.text ||
                        q.name ||
                        `Question ${q.questionId}`}
                      {q.required && (
                        <span style={{ color: COLORS.critical }}> *</span>
                      )}
                    </TrueImpactText>
                    {rendered ? (
                      <TrueImpactText
                        variant="body-small-bold"
                        className="q-answer"
                      >
                        {rendered}
                      </TrueImpactText>
                    ) : (
                      <TrueImpactText
                        variant="body-small-bold"
                        className="q-answer empty"
                      >
                        No answer yet
                      </TrueImpactText>
                    )}
                  </div>
                  <TrueImpactIconButton
                    icon={faPen}
                    label="Edit"
                    onClick={() => onEditQuestion(q.questionId)}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </Wrapper>
  );
};

export default ReviewScreen;
